import { useRef, useMemo } from 'react'
import { Stack, Box, Typography } from '@mui/material'
import type { UseQueryResult } from '@tanstack/react-query'
import { ChartCanvas } from '~/components/Charts/ChartCanvas'
import { findChartRegistryRowForKind } from '~/components/Charts/findChartRegistryRow'
import { ChartRegistryStatusFallback } from '~/components/Charts/ChartRegistryStatusFallback'
import { PinButton } from '~/components/CollectionPin/PinButton'
import { ExportMenu } from '~/components/Export/ExportMenu'
import { ChartCardHeader } from '~/components/Charts/chrome/ChartCardHeader'
import { ChartContextCaption } from '~/components/Charts/chrome/ChartContextCaption'
import { FilterApplicabilityNote, campaignRatioScopeFor } from './FilterApplicabilityNote'
import { ActualAxisNote } from './ActualAxisNote'
import { detectActualAxisDrop } from '~/components/Charts/modelAxisNotice'
import {
  buildChartTitle,
  buildContextCaption,
  timeframePhrase,
  KICKER_BY_CHART_TYPE,
} from '~/components/Charts/chrome/chartChrome'
import { useCampaignHasNoRows } from './useCampaignHasNoRows'
import { buildExportFilenameBase } from '~/util/export/filename'
import {
  useWalkTimeline,
  useWalkAbsolute,
  useWalkRatio,
  useWalkRatioAbsolute,
} from '~/api/hooks/useCharts'
import { useValueTypeLabels } from '~/api/hooks/useValueTypeLabels'
import { useSeriesAdditivity } from '~/components/Charts/useSeriesAdditivity'
import { useColumnsState } from '~/state/useColumnsState'
import { useValueTypeFilterState } from '~/state/useValueTypeFilterState'
import { useDimValues } from '~/api/hooks/useDimValues'
import { useDimValueColorForBase } from '~/components/Charts/useDimValueColorForBase'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'
import type { WalkTimelineRow, WalkAbsoluteRow } from '~/api/endpoints/walks'
import { buildPinV1 } from '~/pin/buildPinV1'
import { ValueTypeFilter } from '~/components/Filters/ValueTypeFilter'
import { baselineBoundaryIso } from '~/components/Charts/dataPipeline'
import { distinctSortedValueTypes } from './distinctSortedValueTypes'
import { useSeriesBaseLabels } from './useSeriesBaseLabels'
import { useTimeframeLabel } from './useTimeframeLabel'
import { useDimValueLabelFor } from './useDimValueLabelFor'
import { resolveEffectiveVts } from './resolveEffectiveVts'
import { WalkError } from './WalkError'
import { CoarseningMarker } from './CoarseningBadge'
import { PartialFyNote } from './PartialFyNote'
import { partialFyLabels } from '~/components/Charts/partialFyLabels'
import type { ReviewPageState } from './useReviewPageState'
import { composeChartTitle } from './composeChartTitle'
import { EmptyChartState } from './EmptyChartState'
import { ChartLoadingState } from './ChartLoadingState'
import { PendingOverlay } from './PendingOverlay'
import { computeSharedDomain } from './sharedYDomain'
import { STACKED_FACETS, type Facet } from '~/state/useChartKnobsState'

export type ChartTileKind = 'timeline' | 'absolute_bar'

interface Props {
  kind: ChartTileKind
  charts: UseQueryResult<ChartRegistryRow[], Error>
  state: ReviewPageState
  pageSlug?: string
}

export function ChartTile(props: Props) {
  if (props.kind === 'timeline') return <TimelineTile {...props} />
  if (props.kind === 'absolute_bar') return <AbsoluteTile {...props} />
  const _exhaustive: never = props.kind
  return _exhaustive
}

function TimelineTile({ charts, state, pageSlug = 'review' }: Props) {
  const tileRef = useRef<HTMLDivElement>(null)
  // A timeline tile is backed by walk_timeline for ordinary kpis, or walk_ratio for
  // the Sales-ratio kpis. The chart_registry row decides which; we fire only the
  // matching walk (the other stays disabled). Ratio walks pass the kpi as p_ratio_kpi
  // and carry no fact_table/aggregation — those params live only in WalkTimelineParams.
  const lookup = findChartRegistryRowForKind(charts, 'walk_timeline', 'walk_ratio')
  const isRatio = lookup.status === 'ok' && lookup.variant === 'ratio'
  // Row 15-light: the plain sub-period timeline is the ONLY walk that extends its `from`
  // (state.baseParams itself is NEVER touched). Compare mode cannot leak in here: PageTiles
  // routes through ComparisonChartTile, which renders this ChartTile only when
  // !compare.enabled — the compare overlays consume state.baseParams directly.
  const baselineFromIso = state.panelBaseline?.baselineFromIso ?? null
  const walkParams = baselineFromIso
    ? { ...state.baseParams, from: baselineFromIso }
    : state.baseParams
  const standardWalk = useWalkTimeline(lookup.status === 'ok' && !isRatio ? walkParams : null)
  const ratioWalk = useWalkRatio(isRatio ? walkParams : null)
  const walk = isRatio ? ratioWalk : standardWalk

  // Window-integrity (row 15-light): with a baseline the walk result is EXTENDED. Every
  // non-chart consumer (vt options, export, empty-check, fanout, shared-Y) sees the
  // IN-WINDOW rows only — totals/pins/exports stay identical with the flag on or off.
  // Boundary rule = splitBaseline semantics on raw rows (bucket start of window-from at
  // the GOVERNED unit; walk rows are bucketed at that unit).
  const boundary = baselineFromIso
    ? baselineBoundaryIso(state.fromIso, state.governance.effectiveUnit)
    : null
  const inWindowData = useMemo(
    () =>
      boundary == null
        ? (walk.data ?? [])
        : (walk.data ?? []).filter((r) => String(r.time) >= boundary),
    [walk.data, boundary],
  )

  // Value type filter — URL-persisted multi-select.
  // allVts: distinct non-null value_types present in the IN-WINDOW walk result.
  // effectiveVts: the user's selection, or all when no selection is made.
  // filteredRows: EXTENDED walk rows narrowed to effectiveVts (null value_type passes
  // through) — the ChartCanvas input; inWindowRows: same filter on in-window rows.
  const [selectedVts, setSelectedVts] = useValueTypeFilterState()
  const allVts = useMemo(() => distinctSortedValueTypes(inWindowData), [inWindowData])
  const effectiveVts = resolveEffectiveVts(selectedVts, allVts)
  const filteredRows = useMemo(
    () =>
      (walk.data ?? []).filter((r) => r.value_type == null || effectiveVts.includes(r.value_type)),
    [walk.data, effectiveVts],
  )
  const inWindowRows = useMemo(
    () => inWindowData.filter((r) => r.value_type == null || effectiveVts.includes(r.value_type)),
    [inWindowData, effectiveVts],
  )

  const vtLabels = useValueTypeLabels(state.factTable)
  const seriesBaseLabelFor = useSeriesBaseLabels(state.factTable, state.groupBy)
  // Chrome inputs (rows 4/5/25) — hooks stay ABOVE the lookup-status early return.
  const mainTimeframeLabel = useTimeframeLabel(state)
  const valueLabelFor = useDimValueLabelFor(state.factTable)

  // Chart-semantics doctrine (spec 2026-08-03 §1) — the resolved verdict for THIS tile's
  // fact/kpi/groupBy/effective-VTs. Replaces the old useIsStackableByKey allow-all default.
  const additivity = useSeriesAdditivity(state.factTable, state.kpi, state.groupBy, effectiveVts)

  // T5: FY honesty marker — pure derivation from the FETCHED window bounds + active unit, no
  // hook needed. Only meaningful at 'fy'; FY is never auto-coarsened away from itself
  // (governance §3), so checking the requested unit here matches the governed unit the chart
  // actually renders at.
  // Final-review I1: the lower bound is the walk's own — baselineFromIso when the previous-period
  // panel is on (that is the window the rendered buckets were filled from), state.fromIso
  // otherwise. A complete fiscal year must never be marked partial; see partialFyLabels' doc.
  const fyLabels =
    state.granularity.unit === 'fy'
      ? partialFyLabels(baselineFromIso ?? state.fromIso, state.toIso)
      : []

  // Columns fanout: when ?columns=<dim> is set AND <dim> is in groupBy, render N tiles
  // (one per dim value) sharing a single walk fetch — rows are filtered client-side.
  // useColumnsState auto-clears when the URL dim is no longer in groupBy.
  const [columnsDim] = useColumnsState(state.groupBy)
  const dimValues = useDimValues(columnsDim, state.factTable, state.kpi)
  // Color catalog (spec D3) — UNCONDITIONAL, single call feeding ALL FOUR ChartCanvas sites
  // below (fanout + non-fanout). Keyed on the FULL state.groupBy (not the fanout-narrowed
  // childGroupBy) — same conservative choice as MetricDriverBarsTile (Task 5): a columns
  // fan-out that consumes the only group-by dim degrades to positional coloring in its
  // per-value tiles rather than attempting to catalog the post-narrowing remainder.
  const colorForBase = useDimValueColorForBase(state.groupBy, state.factTable, state.kpi)
  // D-FEW-5: sharper honest-empty — UNCONDITIONAL, before the lookup-status early return below.
  const campaignHasNoRows = useCampaignHasNoRows(state.campaign, state.factTable, state.kpi)
  if (lookup.status !== 'ok') return <ChartRegistryStatusFallback lookup={lookup} />

  // I1b: detect against the SAME params object the walk above actually fired with
  // (walkParams — extended `from` under a baseline panel, filter_dims/group_by untouched).
  const axisDropDims = detectActualAxisDrop({
    rows: walk.data ?? [],
    filterDims: walkParams.filter_dims,
    groupBy: walkParams.group_by,
  })
  // C3b Task 5: honesty note — set only when this walk is ratio-family AND the active campaign
  // resolved via attribution filters (not the exact Campaign dim). null on every other page.
  // I2/M8: feed the note the walk's actually-applied attribution set (state.campaign.resolvedFilters,
  // already union-pruned) so it never claims a predicate dim the prune dropped.
  const campaignRatioScope = campaignRatioScopeFor(
    isRatio,
    state.campaign,
    state.campaign?.resolvedFilters ?? {},
  )

  const pin = buildPinV1({
    walk_function: isRatio ? 'walk_ratio' : 'walk_timeline',
    fact_table: state.factTable,
    kpi: state.kpi,
    // FY v2: freeze the RAW display unit (not governance.effectiveUnit) — replay must
    // reproduce what THIS tile folds at (ChartCanvas reads the raw unit), spec §3.2.
    render_unit: state.granularity.unit,
    params: {
      from: state.fromIso,
      to: state.toIso,
      // T4b: freeze exactly the walk's fired filter_dims (baseParams — T2 campaign-merged),
      // not the pre-merge state.filters. walkParams never overrides filter_dims (only `from`
      // under a baseline panel), so this is always what the walk above actually fired with.
      filter_dims: state.baseParams.filter_dims,
      group_by: state.groupBy,
      bucket_size: state.baseParams.bucket_size,
      value_types: effectiveVts,
    },
    chart_type: 'timeline',
  })

  const filenameBase = buildExportFilenameBase({
    scope: 'chart',
    page: pageSlug,
    kpi: state.kpi,
    chartType: 'timeline',
  })

  // Chrome (rows 4/5/25): single-vt unit rule — the title names a unit only when unambiguous.
  const singleVtLabel = effectiveVts.length === 1 ? (vtLabels[effectiveVts[0]!] ?? null) : null
  const tfPhrase = timeframePhrase([mainTimeframeLabel])
  const cardTitle = buildChartTitle({
    kpiTitle: composeChartTitle(state.kpiCascade),
    valueTypeLabel: singleVtLabel,
    timeframePhrase: tfPhrase,
  })
  // T4b: caption mirrors the pin — the walk's actual filter_dims, not the pre-merge state.filters.
  const captionLines = buildContextCaption({
    filters: state.baseParams.filter_dims,
    groupBy: state.groupBy,
    valueLabelFor,
  })

  // Fanout branch: render one ChartCanvas per dim value, filtering walk.data client-side.
  // The walk already returns rows with `dims[columnsDim]` populated when columnsDim ∈ groupBy.
  // Tiles are laid out horizontally (direction="row") with a shared Y-domain so visual
  // comparison across panels is meaningful — matching v2's BarChartsWithInnerGroups behavior.
  // TODO if walk-row dims is observed unpopulated for some rows on real data, fall back to
  // per-tile walk-calls (out of scope for Batch B per spec §columns-fanout note).
  if (columnsDim) {
    // Fanout uses IN-WINDOW rows only — the baseline panel is a single-tile affordance;
    // fanned-out mini charts with per-value baselines would be unreadable (and the shared
    // Y-max must not be skewed by pre-window magnitudes).
    const childGroupBy = state.groupBy.filter((d) => d !== columnsDim)
    const dimList = dimValues.data ?? []
    const panels: WalkTimelineRow[][] = dimList.map((value) =>
      inWindowRows.filter((r) => r.dims?.[columnsDim] === value),
    )
    // FU-a: stack-aware shared max, keyed on the RESOLVED DEFAULT facet (per-child knob
    // toggles are accepted-stale — see sharedYDomain.ts).
    const resolvedFacet = (state.decompFacet ?? lookup.row.default_facet ?? 'line') as Facet
    const sharedDomain = computeSharedDomain(
      panels,
      'timeline',
      STACKED_FACETS.includes(resolvedFacet),
    )
    return (
      <Stack spacing={1} ref={tileRef}>
        <ChartCardHeader
          kicker={KICKER_BY_CHART_TYPE['timeline']!}
          title={cardTitle}
          actions={
            <>
              <CoarseningMarker governance={state.governance} />
              <PartialFyNote labels={fyLabels} />
              <ValueTypeFilter options={allVts} selected={selectedVts} onChange={setSelectedVts} />
              <ExportMenu pin={pin} target={() => tileRef.current} filenameBase={filenameBase} />
              <PinButton pin={pin} />
            </>
          }
        />
        {walk.error && !walk.isFetching ? (
          <WalkError error={walk.error} onRetry={() => walk.refetch()} />
        ) : null}
        {!walk.data || walk.error ? (
          walk.isLoading ? (
            <ChartLoadingState />
          ) : null
        ) : inWindowData.length === 0 && !walk.isPlaceholderData ? (
          <EmptyChartState
            fromIso={state.fromIso}
            toIso={state.toIso}
            context="primary"
            campaignLabel={state.campaign?.label ?? null}
            campaignHasNoRows={campaignHasNoRows}
          />
        ) : (
          <PendingOverlay active={walk.isPlaceholderData}>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              {dimList.map((value, idx) => (
                <Box key={value} sx={{ flex: '1 1 0', minWidth: 240 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      {columnsDim}: {seriesBaseLabelFor(value)}
                    </Typography>
                    <ChartCanvas
                      pinId={`${state.pageId}:timeline:col=${value}`}
                      chartType="timeline"
                      rows={panels[idx] ?? []}
                      groupBy={childGroupBy}
                      yLabel={composeChartTitle(state.kpiCascade)}
                      sharedDomain={sharedDomain}
                      vtLabels={vtLabels}
                      additivity={additivity}
                      seriesBaseLabelFor={seriesBaseLabelFor}
                      defaultFacet={state.decompFacet ?? lookup.row.default_facet}
                      isRatio={isRatio}
                      dots={state.granularity.dots}
                      colorForBase={colorForBase}
                      queryFromIso={state.fromIso}
                      queryToIso={state.toIso}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </PendingOverlay>
        )}
        <FilterApplicabilityNote state={state} campaignRatioScope={campaignRatioScope} />
        {axisDropDims && <ActualAxisNote dims={axisDropDims} />}
        <ChartContextCaption lines={captionLines} />
      </Stack>
    )
  }

  return (
    <Stack spacing={1} ref={tileRef}>
      <ChartCardHeader
        kicker={KICKER_BY_CHART_TYPE['timeline']!}
        title={cardTitle}
        actions={
          <>
            <CoarseningMarker governance={state.governance} />
            <PartialFyNote labels={fyLabels} />
            <ValueTypeFilter options={allVts} selected={selectedVts} onChange={setSelectedVts} />
            <ExportMenu pin={pin} target={() => tileRef.current} filenameBase={filenameBase} />
            <PinButton pin={pin} />
          </>
        }
      />
      {walk.error && !walk.isFetching ? (
        <WalkError error={walk.error} onRetry={() => walk.refetch()} />
      ) : null}
      {!walk.data || walk.error ? (
        walk.isLoading ? (
          <ChartLoadingState />
        ) : null
      ) : inWindowData.length === 0 && !walk.isPlaceholderData ? (
        <EmptyChartState
          fromIso={state.fromIso}
          toIso={state.toIso}
          context="primary"
          campaignLabel={state.campaign?.label ?? null}
          campaignHasNoRows={campaignHasNoRows}
        />
      ) : (
        <PendingOverlay active={walk.isPlaceholderData}>
          <ChartCanvas
            pinId={`${state.pageId}:timeline`}
            chartType="timeline"
            // EXTENDED rows — ChartCanvas splits at the boundary and renders the grayed
            // previous-period baseline panel; the IN-WINDOW panels are identical to the flag-off render.
            rows={filteredRows}
            groupBy={state.groupBy}
            yLabel={composeChartTitle(state.kpiCascade)}
            vtLabels={vtLabels}
            additivity={additivity}
            seriesBaseLabelFor={seriesBaseLabelFor}
            defaultFacet={state.decompFacet ?? lookup.row.default_facet}
            isRatio={isRatio}
            queryFromIso={state.fromIso}
            queryToIso={state.toIso}
            dots={state.granularity.dots}
            windowFromIso={baselineFromIso ? state.fromIso : null}
            baselineFromIso={baselineFromIso ?? undefined}
            baselineUnit={state.governance.effectiveUnit}
            colorForBase={colorForBase}
          />
        </PendingOverlay>
      )}
      <FilterApplicabilityNote state={state} campaignRatioScope={campaignRatioScope} />
      {axisDropDims && <ActualAxisNote dims={axisDropDims} />}
      <ChartContextCaption lines={captionLines} />
    </Stack>
  )
}

function AbsoluteTile({ charts, state, pageSlug = 'review' }: Props) {
  const tileRef = useRef<HTMLDivElement>(null)
  // walk_absolute for ordinary kpis, walk_ratio_absolute for the Sales-ratio kpis.
  const lookup = findChartRegistryRowForKind(charts, 'walk_absolute', 'walk_ratio_absolute')
  const isRatio = lookup.status === 'ok' && lookup.variant === 'ratio'
  const standardWalk = useWalkAbsolute(lookup.status === 'ok' && !isRatio ? state.baseParams : null)
  const ratioWalk = useWalkRatioAbsolute(isRatio ? state.baseParams : null)
  const walk = isRatio ? ratioWalk : standardWalk

  // Value type filter — same URL state as TimelineTile (shared param).
  // AbsoluteTile applies the filter but does NOT render the control (only
  // TimelineTile renders it to avoid two identical controls on screen).
  const [selectedVts] = useValueTypeFilterState()
  const allVts = useMemo(() => distinctSortedValueTypes(walk.data ?? []), [walk.data])
  const effectiveVts = resolveEffectiveVts(selectedVts, allVts)
  const filteredRows = useMemo(
    () =>
      (walk.data ?? []).filter((r) => r.value_type == null || effectiveVts.includes(r.value_type)),
    [walk.data, effectiveVts],
  )

  const vtLabels = useValueTypeLabels(state.factTable)
  const seriesBaseLabelFor = useSeriesBaseLabels(state.factTable, state.groupBy)
  // Chrome inputs (rows 4/5/25) — hooks stay ABOVE the lookup-status early return.
  const mainTimeframeLabel = useTimeframeLabel(state)
  const valueLabelFor = useDimValueLabelFor(state.factTable)

  // Chart-semantics doctrine (spec 2026-08-03 §1) — the resolved verdict for THIS tile's
  // fact/kpi/groupBy/effective-VTs. Replaces the old useIsStackableByKey allow-all default.
  const additivity = useSeriesAdditivity(state.factTable, state.kpi, state.groupBy, effectiveVts)

  const [columnsDim] = useColumnsState(state.groupBy)
  const dimValues = useDimValues(columnsDim, state.factTable, state.kpi)
  // Color catalog (spec D3) — UNCONDITIONAL, single call feeding both ChartCanvas sites below
  // (fanout + non-fanout). Same conservative full-groupBy keying as TimelineTile above.
  const colorForBase = useDimValueColorForBase(state.groupBy, state.factTable, state.kpi)
  // D-FEW-5: sharper honest-empty — UNCONDITIONAL, before the lookup-status early return below.
  const campaignHasNoRows = useCampaignHasNoRows(state.campaign, state.factTable, state.kpi)
  if (lookup.status !== 'ok') return <ChartRegistryStatusFallback lookup={lookup} />

  // I1b: AbsoluteTile's walk fires with state.baseParams unadjusted — no baseline extension
  // here (that's TimelineTile-only), so detect against it directly.
  const axisDropDims = detectActualAxisDrop({
    rows: walk.data ?? [],
    filterDims: state.baseParams.filter_dims,
    groupBy: state.baseParams.group_by,
  })
  // C3b Task 5: honesty note — set only when this walk is ratio-family AND the active campaign
  // resolved via attribution filters (not the exact Campaign dim). null on every other page.
  // I2/M8: feed the note the walk's actually-applied attribution set (state.campaign.resolvedFilters,
  // already union-pruned) so it never claims a predicate dim the prune dropped.
  const campaignRatioScope = campaignRatioScopeFor(
    isRatio,
    state.campaign,
    state.campaign?.resolvedFilters ?? {},
  )

  const pin = buildPinV1({
    walk_function: isRatio ? 'walk_ratio_absolute' : 'walk_absolute',
    fact_table: state.factTable,
    kpi: state.kpi,
    // FY v2: freeze the RAW display unit (not governance.effectiveUnit) — replay must
    // reproduce what THIS tile folds at (ChartCanvas reads the raw unit), spec §3.2.
    render_unit: state.granularity.unit,
    params: {
      from: state.fromIso,
      to: state.toIso,
      // T4b: freeze exactly the walk's fired filter_dims (baseParams — T2 campaign-merged),
      // not the pre-merge state.filters. The walk above fires state.baseParams directly.
      filter_dims: state.baseParams.filter_dims,
      group_by: state.groupBy,
      value_types: effectiveVts,
    },
    chart_type: 'absolute_bar',
  })

  const filenameBase = buildExportFilenameBase({
    scope: 'chart',
    page: pageSlug,
    kpi: state.kpi,
    chartType: 'absolute_bar',
  })

  // Chrome (rows 4/5/25): single-vt unit rule — the title names a unit only when unambiguous.
  const singleVtLabel = effectiveVts.length === 1 ? (vtLabels[effectiveVts[0]!] ?? null) : null
  const tfPhrase = timeframePhrase([mainTimeframeLabel])
  const cardTitle = buildChartTitle({
    kpiTitle: composeChartTitle(state.kpiCascade),
    valueTypeLabel: singleVtLabel,
    timeframePhrase: tfPhrase,
  })
  // T4b: caption mirrors the pin — the walk's actual filter_dims, not the pre-merge state.filters.
  const captionLines = buildContextCaption({
    filters: state.baseParams.filter_dims,
    groupBy: state.groupBy,
    valueLabelFor,
  })

  if (columnsDim) {
    const childGroupBy = state.groupBy.filter((d) => d !== columnsDim)
    const dimList = dimValues.data ?? []
    const panels: WalkAbsoluteRow[][] = dimList.map((value) =>
      filteredRows.filter((r) => r.dims?.[columnsDim] === value),
    )
    // FU-a: stack-aware shared max, keyed on the RESOLVED DEFAULT facet (per-child knob
    // toggles are accepted-stale — see sharedYDomain.ts).
    const resolvedFacet = (state.decompFacet ?? lookup.row.default_facet ?? 'line') as Facet
    const sharedDomain = computeSharedDomain(
      panels,
      'absolute_bar',
      STACKED_FACETS.includes(resolvedFacet),
    )
    return (
      <Stack spacing={1} ref={tileRef}>
        <ChartCardHeader
          kicker={KICKER_BY_CHART_TYPE['absolute_bar']!}
          title={cardTitle}
          actions={
            <>
              <ExportMenu pin={pin} target={() => tileRef.current} filenameBase={filenameBase} />
              <PinButton pin={pin} />
            </>
          }
        />
        {walk.error && !walk.isFetching ? (
          <WalkError error={walk.error} onRetry={() => walk.refetch()} />
        ) : null}
        {!walk.data || walk.error ? (
          walk.isLoading ? (
            <ChartLoadingState />
          ) : null
        ) : walk.data.length === 0 && !walk.isPlaceholderData ? (
          <EmptyChartState
            fromIso={state.fromIso}
            toIso={state.toIso}
            context="primary"
            campaignLabel={state.campaign?.label ?? null}
            campaignHasNoRows={campaignHasNoRows}
          />
        ) : (
          <PendingOverlay active={walk.isPlaceholderData}>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              {dimList.map((value, idx) => (
                <Box key={value} sx={{ flex: '1 1 0', minWidth: 240 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      {columnsDim}: {seriesBaseLabelFor(value)}
                    </Typography>
                    <ChartCanvas
                      pinId={`${state.pageId}:absolute_bar:col=${value}`}
                      chartType="absolute_bar"
                      rows={panels[idx] ?? []}
                      groupBy={childGroupBy}
                      yLabel={composeChartTitle(state.kpiCascade)}
                      sharedDomain={sharedDomain}
                      vtLabels={vtLabels}
                      additivity={additivity}
                      seriesBaseLabelFor={seriesBaseLabelFor}
                      defaultFacet={state.decompFacet ?? lookup.row.default_facet}
                      dots={state.granularity.dots}
                      colorForBase={colorForBase}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </PendingOverlay>
        )}
        <FilterApplicabilityNote state={state} campaignRatioScope={campaignRatioScope} />
        {axisDropDims && <ActualAxisNote dims={axisDropDims} />}
        <ChartContextCaption lines={captionLines} />
      </Stack>
    )
  }

  return (
    <Stack spacing={1} ref={tileRef}>
      <ChartCardHeader
        kicker={KICKER_BY_CHART_TYPE['absolute_bar']!}
        title={cardTitle}
        actions={
          <>
            <ExportMenu pin={pin} target={() => tileRef.current} filenameBase={filenameBase} />
            <PinButton pin={pin} />
          </>
        }
      />
      {walk.error && !walk.isFetching ? (
        <WalkError error={walk.error} onRetry={() => walk.refetch()} />
      ) : null}
      {!walk.data || walk.error ? (
        walk.isLoading ? (
          <ChartLoadingState />
        ) : null
      ) : walk.data.length === 0 && !walk.isPlaceholderData ? (
        <EmptyChartState
          fromIso={state.fromIso}
          toIso={state.toIso}
          context="primary"
          campaignLabel={state.campaign?.label ?? null}
          campaignHasNoRows={campaignHasNoRows}
        />
      ) : (
        <PendingOverlay active={walk.isPlaceholderData}>
          <ChartCanvas
            pinId={`${state.pageId}:absolute_bar`}
            chartType="absolute_bar"
            rows={filteredRows}
            groupBy={state.groupBy}
            yLabel={composeChartTitle(state.kpiCascade)}
            vtLabels={vtLabels}
            additivity={additivity}
            seriesBaseLabelFor={seriesBaseLabelFor}
            defaultFacet={state.decompFacet ?? lookup.row.default_facet}
            dots={state.granularity.dots}
            colorForBase={colorForBase}
          />
        </PendingOverlay>
      )}
      <FilterApplicabilityNote state={state} campaignRatioScope={campaignRatioScope} />
      {axisDropDims && <ActualAxisNote dims={axisDropDims} />}
      <ChartContextCaption lines={captionLines} />
    </Stack>
  )
}
